import { isQualitative } from "@/lib/qualitativeRubric";
import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, Save, Zap, Download, Upload } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { BACKEND_BASE_URL } from "../../config/api";
import AdminGuard, { useCanEdit } from "@/components/AdminGuard";
import ProductTypeManager from "@/components/ProductTypeManager";

const BASIC = `${BACKEND_BASE_URL}/api/basic`;
const STAGES = [
  { key: "manufacturing", label: "Manufacturing" },
  { key: "use_phase", label: "Use Phase" },
  { key: "logistics", label: "Logistics" },
];

export default function BasicAdminBenchmarks() {
  const [productTypes, setProductTypes] = useState([]);
  const [selectedPT, setSelectedPT] = useState("");
  const [meta, setMeta] = useState(null);
  const [benchmarks, setBenchmarks] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const canEdit = useCanEdit();

  const refreshProductTypes = async () => {
    try {
      const r = await axios.get(`${BASIC}/product-types`);
      setProductTypes(r.data || []);
    } catch {}
  };

  useEffect(() => {
    (async () => {
      try {
        const [ptRes, metaRes] = await Promise.all([
          axios.get(`${BASIC}/product-types`),
          axios.get(`${BASIC}/meta`),
        ]);
        setProductTypes(ptRes.data);
        setMeta(metaRes.data);
        if (ptRes.data.length > 0) setSelectedPT(ptRes.data[0].id);
      } catch {}
    })();
  }, []);

  useEffect(() => {
    if (!selectedPT) { setBenchmarks({}); return; }
    setLoading(true);
    axios.get(`${BASIC}/product-benchmarks/${selectedPT}`)
      .then(r => setBenchmarks(r.data || {}))
      .catch(() => setBenchmarks({}))
      .finally(() => setLoading(false));
  }, [selectedPT]);

  const updateValue = (stage, type, pk, value) => {
    setBenchmarks(prev => ({
      ...prev,
      [stage]: {
        ...(prev[stage] || { best: {}, worst: {} }),
        [type]: { ...((prev[stage] || {})[type] || {}), [pk]: value === "not_applicable" ? value : (value === "" ? null : Number(value)) }
      }
    }));
  };

  const save = async (stage) => {
    setSaving(true);
    try {
      const bm = benchmarks[stage] || { best: {}, worst: {} };
      await axios.put(`${BASIC}/product-benchmarks/${selectedPT}`, {
        stage, best: bm.best || {}, worst: bm.worst || {}, units: meta.param_units
      });
      toast.success(`Saved ${stage} benchmarks`);
    } catch (e) { toast.error(e.response?.data?.detail?.issues?.[0]?.message || "Save failed"); }
    finally { setSaving(false); }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!selectedPT) { toast.error("Select a product type first"); return; }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await axios.post(`${BASIC}/import-product-benchmarks/${selectedPT}`, fd);
      toast.success(`Imported ${res.data.count} stage benchmarks`);
      // reload
      const r = await axios.get(`${BASIC}/product-benchmarks/${selectedPT}`);
      setBenchmarks(r.data || {});
    } catch (err) {
      toast.error("Import failed: " + (err.response?.data?.detail || err.message));
    }
    if (fileRef.current) fileRef.current.value = "";
  };

  if (!meta) return <div className="flex items-center justify-center h-screen"><Loader2 className="w-8 h-8 animate-spin text-[#43A047]" /></div>;

  return (
    <div className="p-8 max-w-5xl" data-testid="basic-admin-bm">
      <div className="flex items-center gap-3 mb-2">
        <h1 className="text-4xl tracking-tight font-light text-[#1B291D]" style={{ fontFamily: 'Work Sans' }}>Basic Product Benchmarks</h1>
        <Badge className="bg-[#43A047] text-white text-[10px] tracking-wider"><Zap className="w-3 h-3 mr-1" />BASIC</Badge>
      </div>
      <p className="text-sm text-[#617364] mb-4">Enter quantity benchmarks per product. Leave both endpoints blank or choose Not applicable to exclude a measured parameter. Rubric parameters remain active unless marked Not applicable.</p>
      <AdminGuard resource="basic product benchmarks" />

      <div className="mb-6">
        <ProductTypeManager
          apiBase={BASIC}
          productTypes={productTypes}
          canEdit={canEdit}
          onChanged={refreshProductTypes}
          selectedPT={selectedPT}
          onSelectedChange={setSelectedPT}
          modeLabel="BASIC"
        />
      </div>

      <input type="file" ref={fileRef} accept=".xlsx,.xls" onChange={handleImport} className="hidden" />

      <div className="mb-6 flex items-center gap-3">
        <div className="max-w-xs flex-1">
          <Select value={selectedPT} onValueChange={setSelectedPT}>
            <SelectTrigger className="border-[#E0E6E1]" data-testid="basic-pt-select"><SelectValue placeholder="Select product type" /></SelectTrigger>
            <SelectContent>{productTypes.map(pt => <SelectItem key={pt.id} value={pt.id}>{pt.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button variant="outline" size="sm" onClick={() => window.open(`${BASIC}/template/product-benchmarks`, '_blank')} className="border-[#E0E6E1] text-[#617364]" data-testid="basic-bm-template-btn">
          <Download className="w-3.5 h-3.5 mr-1" /> Template
        </Button>
        <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={!selectedPT} className="border-[#E0E6E1] text-[#43A047]" data-testid="basic-bm-import-btn">
          <Upload className="w-3.5 h-3.5 mr-1" /> Import Excel
        </Button>
      </div>

      {!selectedPT ? (
        <Card className="border-[#E0E6E1] bg-white">
          <CardContent className="p-8 text-center text-sm text-[#617364]">
            Select a product above to view and edit its stage-level benchmarks.
          </CardContent>
        </Card>
      ) : loading ? <Loader2 className="w-8 h-8 animate-spin text-[#43A047]" /> : (
        <div className="space-y-6">
          {STAGES.map(st => {
            const bm = benchmarks[st.key] || { best: {}, worst: {} };
            return (
              <Card key={st.key} className="border-[#E0E6E1] bg-white">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base font-medium text-[#1B291D]" style={{ fontFamily: 'Work Sans' }}>{st.label}</CardTitle>
                    <Button variant="outline" size="sm" onClick={() => save(st.key)} disabled={saving || !canEdit} className="border-[#E0E6E1] text-[#43A047] disabled:opacity-50" data-testid={`basic-bm-save-${st.key}`} title={!canEdit ? "Admin access required" : ""}>
                      <Save className="w-3.5 h-3.5 mr-1" /> Save
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader><TableRow className="border-[#E0E6E1]">
                      <TableHead className="text-[10px] uppercase tracking-wider text-[#617364] font-bold w-[220px]">Parameter</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-wider text-[#617364] font-bold">Best</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-wider text-[#617364] font-bold">Worst</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {meta.param_keys.map(pk => (
                        <TableRow key={pk} className="border-[#E0E6E1]">
                          <TableCell className="text-xs text-[#1B291D]">{meta.param_labels[pk]}<span className="text-[10px] text-[#617364] ml-1">({meta.param_units[pk]})</span></TableCell>
                          <TableCell><Select value={bm.best?.[pk] === 'not_applicable' ? 'not_applicable' : 'value'} onValueChange={v => {updateValue(st.key,'best',pk,v === 'value' ? '' : v); updateValue(st.key,'worst',pk,v === 'value' ? '' : v);}}><SelectTrigger className="h-8"><SelectValue/></SelectTrigger><SelectContent><SelectItem value="value">{isQualitative(pk) ? 'Use rubric' : 'Enter value'}</SelectItem><SelectItem value="not_applicable">Not applicable</SelectItem></SelectContent></Select>{!isQualitative(pk) && bm.best?.[pk] !== 'not_applicable' && <Input type="number" step="any" value={typeof bm.best?.[pk] === "number" ? bm.best[pk] : ""} onChange={e => updateValue(st.key, "best", pk, e.target.value)} className="border-[#E0E6E1] h-8 text-sm" />}</TableCell>
                          <TableCell>{!isQualitative(pk) && bm.best?.[pk] !== "not_applicable" && <Input type="number" step="any" value={typeof bm.worst?.[pk] === "number" ? bm.worst[pk] : ""} onChange={e => updateValue(st.key, "worst", pk, e.target.value)} className="border-[#E0E6E1] h-8 text-sm" />}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
